import {UI} from "../../../../../../stem-core/src/ui/UIBase";
import {StyleSheet} from "../../../../../../stem-core/src/ui/Style";

import {styleRule} from "../../../../../../stem-core/src/decorators/Style";
import {Messages} from "../../../../../Messages";
import {registerStyle} from "../../../../../../stem-core/src/ui/style/Theme";
import {iFrameState} from "../../../../../services/IFrameState";
import {Money} from "../../../../../../client/state/misc/Money";
import {MerchantCheckbox} from "../../../../../widget/ui/MerchantCheckbox";
import {LabeledCheckbox} from "../../../../../ui/Checkbox";
import {Flow} from "../../../../initializeFlows";
import {MERCHANT_SCROLLABLE_CONTENT_HEIGHT} from "../../../../../widget/WidgetConstants";
import {isSmallScreen} from "../../../../../UtilsLib";
import {DonateActiveButtonList} from "../../../../../common/donate/DonateActiveButtonList";
import {DonateAmountButtonList} from "../../../../../common/donate/DonateAmountButtonList";
import {queueMicrotaskWrapper} from "../../../../../AsyncAggregateDispatcher";
import {iFrameMerchantService} from "../../../../../services/IframeMerchantService";
import {iFrameUserDataService} from "../../../../../services/IFrameUserDataService";
import {autoredraw} from "../../../../../../stem-core/src/decorators/AutoRedraw";
import {MessageElement} from "../../../../../widget/ui/MessageElement";
import {panelChildMargin} from "../../../../../StyleConstants";


export class DonateComponentStyle extends StyleSheet {
    @styleRule
    element = {
        overflow: () => isSmallScreen() ? "auto" : "",
        maxHeight: () => isSmallScreen() ? MERCHANT_SCROLLABLE_CONTENT_HEIGHT : "",
        paddingRight: () => isSmallScreen() ? 18 : "",
        marginRight: () => isSmallScreen() ? -18 : "",
        ">:not(:last-child)": {
            marginBottom: panelChildMargin,
        }
    };

    @styleRule
    title = this.themeProps.PANEL_TITLE;

    @styleRule
    checkbox = this.themeProps.INLINE_DONATE_FLOW_CHECKBOX;

    @styleRule
    frequencyButtons = {
        fontSize: this.themeProps.MERCHANT_FONT_SIZE_NORMAL,
    };

    @styleRule
    customAmountError = {
        textAlign: "center",
        fontSize: this.themeProps.MERCHANT_FONT_SIZE_SMALL,
        height: 15,
        color: this.themeProps.MERCHANT_ERROR,
    };
}

@autoredraw(iFrameUserDataService)
@registerStyle(DonateComponentStyle)
export class DonateComponent extends UI.Element {
    customInputError = "";
    donationIndex = null;
    defaultCustomAmount = null;

    getDefaultOptions() {
        return {
            ...super.getDefaultOptions(),
            feeCheckboxLabel: Messages.payDonationFeeCheckbox,
            DonateAmountButtonListClass: DonateAmountButtonList,
            DonateActiveButtonListClass: DonateActiveButtonList,
        }
    }

    extraNodeAttributes(attr) {
        super.extraNodeAttributes(attr);
        attr.addClass(this.styleSheet.element);
    }

    formatAmount(amount) {
        return new Money(amount, iFrameMerchantService.getDonationOffer().getCurrency()).toString();
    }

    getDonationIndex() {
        const index = iFrameMerchantService.getDonationOffer().predefinedAmounts.indexOf(Flow.donation.getDonationAmount());
        return index === -1 ? iFrameMerchantService.getDonationOffer().predefinedAmounts.length : index;
    }

    handleDonationAmountChange(amount, isCustomAmount) {
        Flow.donation.donationAmount = amount;
        Flow.donation.isCustomAmount = isCustomAmount;
        this.dispatchChange();
        this.redraw();
    }

    handleFrequencyChange(frequency) {
        Flow.donation.frequency = frequency;
        this.dispatchChange();
        this.redraw();
    }

    // Some of these are called during renders and we want to make sure that the render is done by the time it is
    // dispatched, because the outer components might trigger another render during this dispatch.
    dispatchChange() {
        if (!this.changeDispatchEnqueued) {
            this.changeDispatchEnqueued = true;
            queueMicrotaskWrapper(() => {
                this.changeDispatchEnqueued = false;
                this.dispatch("change");
            });
        }
    }

    recurringOptionsAvailable() {
        return !iFrameUserDataService.getActiveRecurringDonation();
    }

    render() {
        const {styleSheet} = this;
        const {DonateAmountButtonListClass, DonateActiveButtonListClass, feeCheckboxLabel} = this.options;
        const offer = iFrameMerchantService.getDonationOffer();
        const {allowCustomAmount, predefinedAmounts, minAmount, maxAmount} = offer;
        const frequencyOptions = offer.getFrequencyOptions();

        if (this.donationIndex == null) {
            this.donationIndex = this.getDonationIndex();
        }
        const isCustomAmount = this.getDonationIndex() === predefinedAmounts.length;
        if (this.defaultCustomAmount == null) {
            if (isCustomAmount) {
                this.defaultCustomAmount = Flow.donation.donationAmount;
            } else {
                this.defaultCustomAmount = "";
            }
        } else if (!isCustomAmount) {
            this.defaultCustomAmount = "";
        }

        if (!Flow.donation.frequency && !this.recurringOptionsAvailable()) {
            Flow.donation.frequency = frequencyOptions[0];
            this.dispatchChange();
        }
        if (Flow.donation.donationAmount === "") {
            Flow.donation.donationAmount = Flow.donation.getDonationAmount();
            this.dispatchChange();
        }

        const onCustomInputError = (errorMessage) => {
            this.customInputError = errorMessage;
        };

        const onValidCustomInput = () => {
            this.customInputError = "";
            this.redraw();
        };

        const customAmountButtonOptions = {
            currency: iFrameMerchantService.getDonationOffer().getCurrency(),
            minAmount: minAmount,
            maxAmount: maxAmount,
            defaultCustomAmount: this.defaultCustomAmount,
        };

        const showFrequencies = this.recurringOptionsAvailable() && offer.hasFrequencyChoice();

        return [
            offer.hasAmountChoice() || showFrequencies || iFrameState.allowUserToCoverFees ?
                <div className={styleSheet.title}>{Messages.donationDetails}</div> : null,
            offer.hasAmountChoice() ?
            <DonateAmountButtonListClass defaultActiveButtonIndex={this.donationIndex}
                                         items={predefinedAmounts.map(amount => ({
                                                 label: this.formatAmount(amount),
                                                 value: amount,
                                             })
                                         )}
                                         onCustomInputError={onCustomInputError}
                                         onValidCustomInput={onValidCustomInput}
                                         rowSizeLimit={isSmallScreen() ? 3 : 5}
                                         customAmountButtonOptions={allowCustomAmount ? customAmountButtonOptions : null}
                                         onSelect={({amount, isCustomAmount}) => this.handleDonationAmountChange(amount, isCustomAmount)}/>
                                         : null,
            allowCustomAmount ? <div className={styleSheet.customAmountError}>{this.customInputError}</div> : null,
            iFrameState.allowUserToCoverFees ?
                <LabeledCheckbox ref="payDonationFeeCheckbox" className={styleSheet.checkbox}
                                 testId="payDonationFeeCheckbox" CheckboxClass={MerchantCheckbox}
                                 initialValue={Flow.donation.payDonationFeeCheckboxChecked}
                                 label={feeCheckboxLabel(this.formatAmount(Flow.donation.getDonationFee()))}/>
                : null,
            <MessageElement message={Messages.donationExplanationMessage} />,
            showFrequencies ?
                <DonateActiveButtonListClass defaultActiveButtonIndex={frequencyOptions.indexOf(Flow.donation.getFrequency())}
                                             className={styleSheet.frequencyButtons} rowSizeLimit={5}
                                             onSelect={(frequency) => this.handleFrequencyChange(frequency)}
                                             items={frequencyOptions.map(frequency => ({
                                                 label: frequency.getFrequencyName(),
                                                 value: frequency
                                             }))}/>
                : null,
        ];
    }

    onMount() {
        super.onMount();

        if (this.payDonationFeeCheckbox) {
            this.payDonationFeeCheckbox.addChangeListener(enabled => {
                Flow.donation.payDonationFeeCheckboxChecked = enabled;
                this.dispatchChange();
            });
        }
    }
}
